import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Alert, Badge, Button, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDate } from '../people/data';
import { PeopleNav } from '../people/PeopleNav';
import { fieldName, fieldValue, type HistoryEntry, type SupplierDetail, type SupplierValues } from './data';
import { SupplierForm } from './SupplierForm';

const GROUPS: Array<{ title: 'pv.generales' | 'pv.contacto' | 'pv.bancarios'; fields: Array<keyof SupplierValues> }> = [
  { title: 'pv.generales', fields: ['legalName', 'taxId', 'country', 'address'] },
  { title: 'pv.contacto', fields: ['contactName', 'email', 'phone'] },
  { title: 'pv.bancarios', fields: ['bankName', 'bankAccount', 'bankAccountType', 'bankAccountHolder'] },
];

/** Detalle de un proveedor, su edición (IT) y el historial completo: quién cambió qué dato, de qué valor a cuál. */
export function SupplierDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const qc = useQueryClient();
  const supplier = useQuery({ queryKey: ['proveedores', 'detalle', id], queryFn: () => api<SupplierDetail>(`/api/proveedores/${id}`) });
  const history = useQuery({ queryKey: ['proveedores', 'historial', id], queryFn: () => api<HistoryEntry[]>(`/api/proveedores/${id}/historial`) });
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>((location.state as { created?: boolean } | null)?.created ? t('pv.creado_ok') : null);
  const [error, setError] = useState<string | null>(null);

  if (!supplier.data) return supplier.error ? <Alert>{errorMessage(supplier.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const s = supplier.data;

  async function patch(body: object, message: string) {
    await api(`/api/proveedores/${s.id}`, { method: 'PATCH', json: body });
    await qc.invalidateQueries({ queryKey: ['proveedores'] });
    setNotice(message);
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/proveedores" className="text-sm text-marca hover:underline">
          ← {t('pv.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          {s.tradeName}
          {!s.active && (
            <span className="ml-2 align-middle">
              <Badge>{t('pv.inactivo')}</Badge>
            </span>
          )}
        </h1>
        <p className="text-sm text-slate-600">
          {t('pv.creado_por', { quien: s.createdByName ?? '—', fecha: formatDate(s.createdAt) })}
          {s.updatedByName && s.updatedAt !== s.createdAt && ` · ${t('pv.editado_por', { quien: s.updatedByName, fecha: formatDate(s.updatedAt) })}`}
        </p>
      </div>
      <PeopleNav />
      {notice && <Alert tone="success">{notice}</Alert>}
      {error && <Alert>{error}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[1fr_24rem]">
        <div className="space-y-4">
          {editing ? (
            <Card title={t('pv.editar')}>
              <SupplierForm
                initial={s}
                submitLabel={t('comun.guardar')}
                onCancel={() => setEditing(false)}
                onSubmit={async (values) => {
                  await patch(values, t('pv.guardado_ok'));
                  setEditing(false);
                }}
              />
            </Card>
          ) : (
            <Card>
              <div className="space-y-4">
                {GROUPS.map((g) => (
                  <section key={g.title}>
                    <h2 className="mb-2 text-sm font-semibold text-slate-900">{t(g.title)}</h2>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                      {g.fields.map((f) => (
                        <div key={f} className="contents">
                          <dt className="text-slate-500">{fieldName(f)}</dt>
                          <dd className={f === 'taxId' || f === 'bankAccount' ? 'font-mono' : ''}>{fieldValue(f, s[f])}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                ))}
                {s.notes && <p className="whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-700">{s.notes}</p>}
                {s.can.manage && (
                  <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-3">
                    <Button variant="link" className="text-sm" onClick={() => setEditing(true)}>
                      {t('pv.editar')}
                    </Button>
                    <Button
                      variant="link"
                      className="text-sm"
                      onClick={() => {
                        setError(null);
                        void patch({ active: !s.active }, s.active ? t('pv.desactivado_ok') : t('pv.activado_ok')).catch((err: unknown) => setError(errorMessage(err)));
                      }}
                    >
                      {s.active ? t('pv.desactivar') : t('pv.activar')}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
        <Card variant="aside" title={t('pv.historial')}>
          {!history.data ? (
            history.error ? <Alert>{errorMessage(history.error)}</Alert> : <Spinner label={t('comun.cargando')} />
          ) : (
            <ol className="space-y-3" aria-label={t('pv.historial')}>
              {history.data.map((h) => (
                <li key={h.id} className="rounded-md border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-900">
                    {t(h.action === 'proveedores.creado' ? 'pv.h_creado' : h.action === 'proveedores.datos_bancarios' ? 'pv.h_bancarios' : 'pv.h_actualizado', { quien: h.actor ?? '—' })}
                  </p>
                  <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString('es-PA', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  {h.action !== 'proveedores.creado' && (
                    <ul className="mt-2 space-y-1">
                      {h.changes.map((c) => (
                        <li key={c.field} className={c.bank ? 'rounded bg-amber-50 px-2 py-1' : ''}>
                          <span className="font-medium text-slate-700">{fieldName(c.field)}:</span> <span className="text-slate-500 line-through">{fieldValue(c.field, c.before)}</span>{' '}
                          → <span className="text-slate-900">{fieldValue(c.field, c.after)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </div>
  );
}
